import { lazy, Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query'
import { useAuthStore } from './store'

import MainLayout from './components/MainLayout'

const queryClient = new QueryClient()
const LoginPage = lazy(() => import('./pages/LoginPage'))
const RegisterPage = lazy(() => import('./pages/RegisterPage'))
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'))
const SignContractPage = lazy(() => import('./pages/SignContractPage'))
const ApproveQuotePage = lazy(() => import('./pages/ApproveQuotePage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const CompaniesPage = lazy(() => import('./pages/CompaniesPage'))
const ContactsPage = lazy(() => import('./pages/ContactsPage'))
const PipelinePage = lazy(() => import('./pages/PipelinePage'))
const ProductsPage = lazy(() => import('./pages/ProductsPage'))
const TasksPage = lazy(() => import('./pages/TasksPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const CompanyPage = lazy(() => import('./pages/CompanyPage'))
const QuotesPage = lazy(() => import('./pages/QuotesPage'))
const ContractsPage = lazy(() => import('./pages/ContractsPage'))
const SubscriptionsPage = lazy(() => import('./pages/SubscriptionsPage'))
const BillingPage = lazy(() => import('./pages/BillingPage'))
const PlatformAdminPage = lazy(() => import('./pages/PlatformAdminPage'))

const PageFallback = () => (
  <div className='flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-500'>
    Loading workspace...
  </div>
)

function App() {
  const token = useAuthStore((state) => state.token)
  const isAuthenticated = !!token

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path='/login' element={isAuthenticated ? <Navigate to='/' replace /> : <LoginPage />} />
            <Route path='/register' element={isAuthenticated ? <Navigate to='/' replace /> : <RegisterPage />} />
            <Route
              path='/accept-invite'
              element={isAuthenticated ? <Navigate to='/' replace /> : <AcceptInvitationPage />}
            />
            <Route path='/sign-contract' element={<SignContractPage />} />
            <Route path='/approve-quote' element={<ApproveQuotePage />} />

            {isAuthenticated && (
              <Route element={<MainLayout />}>
                <Route path='/' element={<DashboardPage />} />
                <Route path='/companies' element={<CompaniesPage />} />
                <Route path='/contacts' element={<ContactsPage />} />
                <Route path='/pipeline' element={<PipelinePage />} />
                <Route path='/products' element={<ProductsPage />} />
                <Route path='/tasks' element={<TasksPage />} />
                <Route path='/settings' element={<SettingsPage />} />
                <Route path='/company' element={<CompanyPage />} />
                <Route path='/quotes' element={<QuotesPage />} />
                <Route path='/contracts' element={<ContractsPage />} />
                <Route path='/subscriptions' element={<SubscriptionsPage />} />
                <Route path='/billing' element={<BillingPage />} />
                <Route path='/admin' element={<PlatformAdminPage />} />
              </Route>
            )}

            <Route path='*' element={<Navigate to={isAuthenticated ? '/' : '/login'} replace />} />
          </Routes>
        </Suspense>
      </Router>
    </QueryClientProvider>
  )
}

export default App
